import { DocumentReference, DocumentSnapshot } from "@atgof-firebase/types";
import { hasSubject, subjectRef, SubjectSpec } from "./subject";
import { OnNext, onSnapshots } from "./onSnapshot";
import { onSceneSheets, SceneSheets, SheetCategory } from "./sheet";
import { EpisodePath, getEpisodePath, perEpisodeRef } from "./episode";
import { onScenesInParent } from "./scene";
import _ from "lodash";
import { notDeleted } from "./general";
import { applicandCollectionName, ApplicandKind } from "./applicand";

export type EpisodeBreakdown = {
  episodeLookSheets: Record<string, DocumentSnapshot>;
};

export function getProjectRef(
  episodeRef: DocumentReference,
  episodePath: EpisodePath = getEpisodePath(episodeRef),
) {
  return episodePath.project
    ? episodeRef.firestore.collection("projects").doc(episodePath.project)
    : undefined;
}

export function onEpisodeSceneSheets(
  episodeRef: DocumentReference,
  categories: readonly SheetCategory[],
  subject: SubjectSpec,
  onNext: OnNext<SceneSheets[] | undefined>,
) {
  const projectRef = getProjectRef(episodeRef, getEpisodePath(episodeRef));
  return onScenesInParent(
    episodeRef,
    (scenes) => onSceneSheets(projectRef, categories, subject, scenes, onNext),
    (q) => hasSubject(q, subject),
  );
}

export function getEpisodeApplicandsRef(
  episodeRef: DocumentReference,
  applicandKind: ApplicandKind,
  subject: SubjectSpec,
) {
  const episodePath = getEpisodePath(episodeRef);
  return perEpisodeRef(
    subjectRef(getProjectRef(episodeRef, episodePath), subject),
    episodePath,
  )?.collection(applicandCollectionName(applicandKind));
}

export function onEpisodeApplicandSheets(
  episodeRef: DocumentReference,
  applicandKind: ApplicandKind,
  categories: readonly SheetCategory[],
  subject: SubjectSpec,
  onNext: OnNext<Record<SheetCategory, DocumentSnapshot[]> | undefined>,
) {
  const episodeApplicandsRef = getEpisodeApplicandsRef(
    episodeRef,
    applicandKind,
    subject,
  );
  if (!episodeApplicandsRef) return;
  return onSnapshots<SheetCategory, Record<SheetCategory, DocumentSnapshot[]>>(
    categories,
    (category) => {
      return (onNext) =>
        notDeleted(episodeApplicandsRef.where("category", "==", category))
          .orderBy("order", "asc")
          .onSnapshot((snapshot) =>
            onNext({ [category]: snapshot.docs } as Record<
              SheetCategory,
              DocumentSnapshot[]
            >),
          );
    },
    (entries) =>
      onNext(
        entries?.reduce((acc, entry) => ({ ...acc, ...entry }), {}) as Record<
          SheetCategory,
          DocumentSnapshot[]
        >,
      ),
  );
}
